import { createApi } from "@reduxjs/toolkit/query/react";
import { createBaseQuery } from "@/shared/utils/baseQuery";

export type VideoAnalysisStatus = "processing" | "done" | "error" | null;

export interface UploadVideoRequest {
  video: File;
  schedule_id: number;
  exercise_sample_id: number;
  exercise_type?: string;
}

export interface UploadVideoResponse {
  status: string;
  record_id: number;
  neural_video_id: string;
}

export interface GetVideoAnalysisStatusRequest {
  schedule_id: number;
  exercise_sample_id: number;
}

export interface AnalysisRep {
  rep: number;
  classification: "good" | "bad";
  errors: string[];
  angles: {
    hip: number;
    knee: number;
    ankle: number;
  };
  svm_pred: string;
  svm_confidence: number;
  gru_pred: string;
}

export interface AnalysisResultJson {
  video_id: string;
  exercise: string;
  status: string;
  classification: "good" | "bad";
  total_reps: number;
  reps: AnalysisRep[];
}

export interface GetVideoAnalysisStatusResponse {
  status: VideoAnalysisStatus;
  result_json: AnalysisResultJson | null;
  result_video_url: string | null;
  record_id: number | null;
}

export const videoAnalysisApi = createApi({
  reducerPath: "videoAnalysisApi",
  baseQuery: createBaseQuery("https://api.fitkeep.ru/"),
  endpoints: (builder) => ({
    uploadVideoForAnalysis: builder.mutation<UploadVideoResponse, UploadVideoRequest>({
      query: ({ video, schedule_id, exercise_sample_id, exercise_type }) => {
        const formData = new FormData();
        formData.append("video", video);
        formData.append("schedule_id", String(schedule_id));
        formData.append("exercise_sample_id", String(exercise_sample_id));
        if (exercise_type) {
          formData.append("exercise_type", exercise_type);
        }
        return {
          url: "/video-analysis/upload",
          method: "POST",
          body: formData,
        };
      },
    }),

    getVideoAnalysisStatus: builder.query<
      GetVideoAnalysisStatusResponse,
      GetVideoAnalysisStatusRequest
    >({
      query: ({ schedule_id, exercise_sample_id }) => ({
        url: `/video-analysis/status/${schedule_id}/${exercise_sample_id}`,
        method: "GET",
      }),
    }),
  }),
});

export const {
  useUploadVideoForAnalysisMutation,
  useGetVideoAnalysisStatusQuery,
  useLazyGetVideoAnalysisStatusQuery,
} = videoAnalysisApi;
